<template>
    <span class="token-wrapper">
        <span
            v-for="(bracket, i) in startRange"

            class="bracket"

            :style="{ color: getBracketColor(bracket.entity) }"
            :key="`start-${i}`"
        >
            {
        </span>

        <span
            class="token"
        >
            {{ token.value }}
        </span>

        <span
            v-for="(bracket, i) in endRange"
            :key="`end-${i}`"
            :style="{ color: getBracketColor(bracket.entity) }"
        >
            <span
                ref="label"
                class="bracket-label"
            >
                {{ bracket.entity || bracket.name }}
            </span>
            <span class="bracket">
                }
            </span>
        </span>

        <span
            v-if="token.spaced"
            class="space"
        >{{' '}}</span>
    </span>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { stringToHexColor } from '@/utils/utils'
import directives from '@/components/common/utterance/directives'
import { ENTITY_TYPE } from '@/components/model/nlu-model/sidebar/entities/entity-types'

export default {
  directives,

  props: {
    token: Object,
    index: Number,

    entities: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      selectedToken: {
        start: null,
        end: null
      }
    }
  },

  computed: {
    ...mapState('knowledgebase/entities', ['machineLearningEntities']),
    ...mapGetters('knowledgebase', ['getCurrent']),

    modelExtensions () {
      return _.get(this.getCurrent, 'extensions')
    },

    mlEntities () {
      const addParentToChildEntity = entity =>
        entity.children && entity.children.map(childEntity => {
          childEntity = { ...childEntity }
          childEntity.parent = entity
          childEntity.children = addParentToChildEntity(childEntity)
          return childEntity
        })

      return this.machineLearningEntities.value.map(entity => {
        entity = { ...entity }
        entity.children = addParentToChildEntity(entity)
        return entity
      })
    },

    entitiesGroups () {
      const localMLEntities = this.localEntities.filter(entity => entity.type === ENTITY_TYPE.machineLearning)

      const getNotCoveredEntities = (groupName, selectedEntities, allEntities) => {
        const groups = []
        const coveredMLEntities = []

        selectedEntities.forEach(entity => {
          const doesEntityCoversSelection =
                        entity.start <= this.selectedToken.start &&
                        entity.end >= this.selectedToken.end

          if (doesEntityCoversSelection) {
            coveredMLEntities.push(entity)
          }
        })

        const notCoveredMLEntities = allEntities
          .filter(mlEntity => !coveredMLEntities.some(entity => entity.entity === mlEntity.name))

        if (notCoveredMLEntities.length !== 0) {
          groups.push({
            groupName,
            entities: notCoveredMLEntities
          })
        }

        coveredMLEntities.forEach(entity => {
          const possibleEntityOptions = allEntities.find(e => e.name === entity.entity)
          // Can happen if we f(a) delete entity (b) rename it (in ML editor)
          if (!possibleEntityOptions) {
            console.debug('Looks like entity does not exists anymore:', entity, allEntities)
            return
          }
          if (!entity.children || entity.children.length === 0) {
            groups.push({
              groupName: entity.entity,
              entities: possibleEntityOptions.children
            })
          }
          groups.push(...getNotCoveredEntities(entity.entity, entity.children, possibleEntityOptions.children))
        })

        return groups
      }

      return _.uniqBy(
        getNotCoveredEntities(
          'ML Entities',
          localMLEntities.filter(entity => !entity.virtual).map(({ virtual, ...rest }) => rest),
          this.mlEntities
        ),
        'groupName'
      )
    },

    localEntities () {
      const entities = []
      this.entities.forEach(entity => {
        const { type } = entity
        const stack = [{ ...entity, virtual: false }]
        while (stack.length) {
          const el = stack.pop()
          entities.push({
            virtual: true,
            ...el,
            type
          })
          if (el.children) {
            stack.push(...el.children)
          }
        }
      })
      return entities
    },

    startRange () {
      return _
        .chain(this.localEntities)
        .filter(['start', this.index])
        .sortBy(entity => {
          if (entity.type !== ENTITY_TYPE.machineLearning) {
            return -1
          }
          let depth = 0
          while (entity.children && entity.children.length !== 0) {
            entity = entity.children[0]
            depth++
          }
          return depth
        })
        .sortBy(({ start, end }) => end - start)
        .reverse()
        .value()
    },

    endRange () {
      return _
        .chain(this.localEntities)
        .filter(['end', this.index])
        .sortBy(entity => {
          if (entity.type !== ENTITY_TYPE.machineLearning) {
            return -1
          }
          let depth = 0
          while (entity.children && entity.children.length !== 0) {
            entity = entity.children[0]
            depth++
          }
          return depth
        })
        .sortBy(({ start, end }) => end - start)
        .value()
    }
  },

  methods: {
    getBracketColor (entityName) {
      return stringToHexColor(entityName)
    }
  }
}
</script>

<style lang="scss" scoped>
.entity-group--name {
    padding: 8px;
    color: rgb(145, 150, 157);
    font-size: 14px;
    font-weight: normal;
}

.bracket {
    line-height: 1.8;
    font-size: 14px;
    font-weight: bold;

    .bracket-label {
        border-style: solid;
        border-width: 1px;
        border-radius: 3px;
        margin: 0 3px;
        padding: 2px 4px;
        font-size: 14px;
        font-weight: normal;
    }
}

.start-selected, .end-selected {
    .token::before, .token::after {
        color:#409fed;
        font-size: 14px;
        font-weight: bold;
    }
}

.start-selected .token:before {
    content: "{"
}

.end-selected .token:after {
    content: "}"
}
</style>
